<template>
  <div class="p-8 bg-[#F8FAFC] min-h-screen">
    <!-- Header -->
    <header class="flex lg:flex-row justify-between items-start lg:items-center mb-8 gap-6">
      <div class="flex items-center gap-4">
        <div>
          <h1 class="text-2xl font-black text-gray-900 uppercase tracking-tight">
            Poste : <span class="text-blue-600">{{ currentCounterName || 'Aucun Guichet' }}</span>
          </h1>
          <p class="text-sm text-gray-500 font-medium italic">
            {{ userRole === 'AGENCY_ADMIN' ? 'Administrateur' : 'Agent' }}
          </p>
        </div>
      </div>

      <div class="flex items-center gap-4">
        <!-- Counter Selection for Admin or Help Mode -->
        <div v-if="userRole === 'AGENCY_ADMIN' || helpMode" class="flex items-center gap-3">
          <select
            v-model="selectedCounterId"
            @change="onCounterChange"
            class="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-blue-500 focus:outline-none">
            <option value="">Sélectionner un guichet</option>
            <option v-for="counter in counters" :key="counter._id" :value="counter._id">
              {{ counter.name }}
            </option>
          </select>
        </div>

        <!-- Help Mode Toggle for Employee -->
        <button
          v-if="userRole === 'EMPLOYEE' && !helpMode"
          @click="enterHelpMode"
          class="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-bold text-sm transition-all">
          🤝 Aider un autre guichet
        </button>

        <!-- Exit Help Mode -->
        <button
          v-if="helpMode"
          @click="exitHelpMode"
          class="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-xl font-bold text-sm transition-all">
          ← Retour à mon guichet
        </button>

        <!-- WebSocket Status -->
        <div class="text-right hidden md:block">
          <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Statut Connexion</p>
          <p class="text-sm font-bold flex items-center gap-2" :class="isSocketConnected ? 'text-green-500' : 'text-red-500'">
            <span class="w-2 h-2 rounded-full" :class="isSocketConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'"></span>
            {{ isSocketConnected ? 'Temps Réel Actif' : 'Reconnexion...' }}
          </p>
        </div>
      </div>
    </header>

    <!-- No Counter Selected Warning -->
    <div v-if="!selectedCounterId"
         class="bg-blue-50 border-2 border-blue-200 rounded-[2rem] p-8 flex flex-col items-center text-center shadow-lg shadow-blue-100">
      <div class="w-16 h-16 bg-blue-200 rounded-full flex items-center justify-center text-blue-600 mb-4">
        <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-width="3" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <h2 class="text-xl font-black text-blue-900 uppercase">
        {{ userRole === 'AGENCY_ADMIN' ? 'Sélectionnez un guichet' : 'Aucun guichet assigné' }}
      </h2>
      <p class="text-blue-700 text-sm font-medium mt-1">
        {{ userRole === 'AGENCY_ADMIN' ? 'Choisissez un guichet dans le menu ci-dessus' : 'Contactez votre administrateur pour être assigné' }}
      </p>
    </div>

    <!-- Main Content -->
    <div v-else class="grid xl:grid-cols-3 gap-8">
      <!-- Left: Current Ticket & Actions -->
      <div class="xl:col-span-2 space-y-6">
        <!-- Empty State -->
        <div v-if="waitingTickets.length === 0 && !currentTicket"
             class="bg-blue-50 border-2 border-blue-200 rounded-[2rem] p-8 flex flex-col items-center text-center shadow-lg shadow-blue-100">
          <div class="w-16 h-16 bg-blue-200 rounded-full flex items-center justify-center text-blue-600 mb-4">
            <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="3" d="M5 13l4 4L19 7" /></svg>
          </div>
          <h2 class="text-xl font-black text-blue-900 uppercase">File d'attente vide</h2>
          <p class="text-blue-700 text-sm font-medium mt-1">Tous les clients ont été servis.</p>
        </div>

        <!-- Current Ticket Display -->
        <div v-else class="bg-white rounded-[2.5rem] shadow-xl shadow-gray-200/40 p-8 border border-gray-50 relative overflow-hidden">
          <div class="flex justify-between items-center mb-8">
            <div class="flex flex-col">
              <span class="text-[10px] font-black text-gray-400 uppercase tracking-widest">En cours de traitement</span>
              <h2 class="text-xl font-black text-gray-800 uppercase">{{ currentTicket?.queueId?.name || 'Service' }}</h2>
            </div>
            <span class="px-4 py-2 bg-gray-900 text-white rounded-xl text-xs font-black tracking-widest uppercase">
              Ticket #{{ currentTicket?._id ? currentTicket._id.slice(-4) : '0000' }}
            </span>
          </div>

          <!-- Large Ticket Number Display -->
          <div class="flex flex-col items-center justify-center py-10 bg-[#222222] rounded-[2.5rem] mb-8 shadow-inner">
            <p class="text-[10px] font-black text-blue-400 uppercase tracking-[0.4em] mb-2">Numéro d'appel</p>
            <div class="text-9xl font-black text-white tabular-nums tracking-tighter">
              {{ currentTicket?.number || '---' }}
            </div>
            <div class="mt-6 flex gap-3">
              <span class="px-3 py-1 bg-white/10 rounded-lg text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                {{ currentTicket?.status === 'CALLED' ? 'Appelé' : 'En attente' }}
              </span>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="grid md:grid-cols-3 gap-4">
            <!-- Recall Button -->
            <button
              @click="recallTicket(currentTicket)"
              :disabled="!isSocketConnected"
              class="flex flex-col items-center p-6 bg-gray-50 rounded-3xl border border-gray-100 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all group">
              <svg class="w-6 h-6 mb-2 text-gray-400 group-hover:text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-width="2" d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M5 9v6h4l5 5V4L9 9H5z" />
              </svg>
              <span class="text-[10px] font-black uppercase tracking-widest">Rappeler</span>
            </button>

            <!-- Call Next Button -->
            <button
              @click="callNext"
              :disabled="isLoading || !isSocketConnected"
              class="flex flex-col items-center p-6 bg-blue-600 text-white rounded-3xl shadow-lg shadow-blue-200 hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
              <svg v-if="!isLoading" class="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-width="3" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
              <span v-else class="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-2"></span>
              <span class="text-sm font-black uppercase tracking-widest">Suivant</span>
            </button>

            <!-- Mark Absent Button -->
            <button
              @click="markAbsent(currentTicket)"
              :disabled="!isSocketConnected"
              class="flex flex-col items-center p-6 bg-gray-50 rounded-3xl border border-gray-100 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed group transition-all">
              <svg class="w-6 h-6 mb-2 text-gray-400 group-hover:text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span class="text-[10px] font-black uppercase tracking-widest group-hover:text-red-500">Absent</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Right: Waiting Queue -->
      <div class="space-y-6">
        <div class="bg-white rounded-[2.5rem] p-8 border border-gray-100 shadow-sm">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">Prochains clients</h3>
            <span class="bg-blue-600 text-white text-[10px] font-black px-2 py-1 rounded-md">{{ waitingTickets.length }}</span>
          </div>

          <!-- Waiting Tickets List -->
          <div class="space-y-3 max-h-[500px] overflow-y-auto pr-2">
            <div v-for="(ticket) in waitingTickets" :key="ticket._id"
                 class="p-4 rounded-2xl border border-gray-50 bg-gray-50/50 flex justify-between items-center group hover:bg-white hover:border-blue-100 transition-all">
              <div class="flex-1">
                <p class="text-sm font-black text-gray-800">{{ ticket.number }}</p>
                <p class="text-[9px] font-bold text-gray-400 uppercase">{{ ticket.queueId?.name || 'Accueil' }}</p>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-lg">
                  {{ getWaitTime(ticket.createdAt) }} min
                </span>
                <button
                  @click="callSpecificTicket(ticket)"
                  :disabled="!isSocketConnected"
                  class="opacity-0 group-hover:opacity-100 bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Appeler ce ticket"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-width="2" d="M15 19l-7-7 7-7" transform="rotate(180 12 12)" />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Empty State -->
            <div v-if="waitingTickets.length === 0" class="text-center py-10 text-gray-300 font-bold uppercase text-[10px]">
              Aucun ticket
            </div>
          </div>
        </div>

        <!-- Recent Calls from Other Counters -->
        <div class="bg-white rounded-[2.5rem] p-8 border border-gray-100 shadow-sm mt-6">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">Autres Caisses</h3>
            <span class="bg-purple-600 text-white text-[10px] font-black px-2 py-1 rounded-md">{{ recentOtherCounterCalls.length }}</span>
          </div>

          <div class="space-y-3 max-h-[300px] overflow-y-auto pr-2">
            <div v-for="ticket in recentOtherCounterCalls" :key="ticket._id"
                 class="p-3 rounded-2xl border border-purple-100 bg-purple-50/30 flex flex-col">
              <div class="flex justify-between items-center mb-1">
                <p class="text-sm font-black text-gray-800">{{ ticket.number }}</p>
                <span class="text-[9px] font-bold text-purple-600 bg-purple-100 px-2 py-0.5 rounded-lg">
                  {{ ticket.counterName || 'Caisse' }}
                </span>
              </div>
              <div class="flex justify-between items-center">
                <p class="text-[9px] font-bold text-gray-400 uppercase">{{ ticket.queueId?.name || 'N/A' }}</p>
                <span class="text-[9px] font-bold text-gray-400">
                  {{ formatCallTime(ticket.calledAt) }}
                </span>
              </div>
            </div>

            <div v-if="recentOtherCounterCalls.length === 0" class="text-center py-6 text-gray-300 font-bold uppercase text-[10px]">
              Aucun appel récent
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notifications -->
    <div v-if="notification"
         class="fixed bottom-8 right-8 bg-slate-900 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 z-50 animate-slide-in">
      <span class="text-2xl">{{ notification.icon }}</span>
      <div>
        <p class="font-black text-sm">{{ notification.title }}</p>
        <p class="text-xs opacity-80">{{ notification.message }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { io } from 'socket.io-client';
import axios from 'axios';

// API Configuration
const API_URL = 'https://api.datafuse.fr/api/agency';
const WS_URL = 'https://api.datafuse.fr';

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.authorization = `Bearer ${token}`;
  }
  return config;
});

// User Info
const userId = localStorage.getItem('userId');
const userRole = ref(localStorage.getItem('userRole'));
const agencyId = localStorage.getItem('agencyId');
const token = localStorage.getItem('token');

// State
const counters = ref([]);
const selectedCounterId = ref('');
const currentCounterName = ref('');
const helpMode = ref(false);
const isLoading = ref(false);
const currentTicket = ref(null);
const waitingTickets = ref([]);
const recentOtherCounterCalls = ref([]);
const notification = ref(null);
const isSocketConnected = ref(false);

// WebSocket
let socket = null;

// Initialize
onMounted(async () => {
  await fetchCounters();

  // Auto-select counter for EMPLOYEE
  if (userRole.value === 'EMPLOYEE') {
    await autoSelectEmployeeCounter();
  }

  initWebSocket();
});

onUnmounted(() => {
  if (socket) {
    socket.disconnect();
  }
});

// Fetch Counters
const fetchCounters = async () => {
  try {
    const response = await api.get('/counters');
    counters.value = response.data;
    console.log('✅ Guichets récupérés:', counters.value.length);
  } catch (error) {
    console.error('❌ Erreur récupération guichets:', error);
    showNotification('❌', 'Erreur', 'Impossible de charger les guichets');
  }
};

// Auto-select counter for employee based on assignedCounterId
const autoSelectEmployeeCounter = async () => {
  try {
    const response = await api.get('/users');
    const currentUser = response.data.find(u => u._id === userId);
    console.log(response.data);

    if (currentUser && currentUser.assignedCounterId) {
      const counterId = currentUser.assignedCounterId._id || currentUser.assignedCounterId;
      selectedCounterId.value = counterId;

      const counter = counters.value.find(c => c._id === counterId);
      if (counter) {
        currentCounterName.value = counter.name;
        console.log('✅ Guichet auto-sélectionné:', counter.name);
        fetchWaitingTickets();
      }
    }
  } catch (error) {
    console.error('❌ Erreur auto-sélection guichet:', error);
  }
};

// Counter change handler
const onCounterChange = () => {
  const counter = counters.value.find(c => c._id === selectedCounterId.value);
  if (counter) {
    currentCounterName.value = counter.name;
    currentTicket.value = null;
    fetchWaitingTickets();
    console.log('🔄 Guichet changé:', counter.name);
  }
};

// Enter Help Mode (Employee only)
const enterHelpMode = () => {
  helpMode.value = true;
  selectedCounterId.value = '';
  currentCounterName.value = '';
  currentTicket.value = null;
  waitingTickets.value = [];
  showNotification('🤝', 'Mode Aide', 'Sélectionnez le guichet à aider');
};

// Exit Help Mode (Return to assigned counter)
const exitHelpMode = async () => {
  helpMode.value = false;
  selectedCounterId.value = '';
  currentCounterName.value = '';
  currentTicket.value = null;
  waitingTickets.value = [];
  await autoSelectEmployeeCounter();
  showNotification('✅', 'Retour', 'Vous êtes de retour à votre guichet');
};

// Fetch Waiting Tickets for selected counter
const fetchWaitingTickets = async () => {
  if (!selectedCounterId.value) return;

  try {
    const response = await api.get(`/tickets?counterId=${selectedCounterId.value}&status=WAITING`);
    waitingTickets.value = response.data;
    console.log(response.data)
    // Get current ticket (CALLED status)
    const calledResponse = await api.get(`/tickets?counterId=${selectedCounterId.value}&status=CALLED`);
    if (calledResponse.data && calledResponse.data.length > 0) {
      currentTicket.value = calledResponse.data[0];
    } else {
      currentTicket.value = null;
    }

    console.log('✅ Tickets en attente:', waitingTickets.value.length);

    // Fetch recent calls from other counters
    await fetchRecentOtherCounterCalls();
  } catch (error) {
    console.error('❌ Erreur récupération tickets:', error);
  }
};

// Fetch recent calls from OTHER counters (not current counter)
const fetchRecentOtherCounterCalls = async () => {
  if (!selectedCounterId.value || !agencyId) return;

  try {
    const response = await api.get(`/tickets?agencyId=${agencyId}&status=CALLED`);
    // Filter out tickets from current counter and keep only last 4
    const otherCounterTickets = response.data
      .filter(ticket => ticket.counterId && ticket.counterId !== selectedCounterId.value)
      .sort((a, b) => new Date(b.calledAt) - new Date(a.calledAt))
      .slice(0, 4);

    recentOtherCounterCalls.value = otherCounterTickets;
    console.log('✅ Derniers appels autres caisses:', otherCounterTickets.length);
  } catch (error) {
    console.error('❌ Erreur récupération appels autres caisses:', error);
  }
};

// Call Next Ticket
const callNext = () => {
  if (!selectedCounterId.value) {
    showNotification('⚠️', 'Attention', 'Aucun guichet sélectionné');
    return;
  }

  if (!socket || !isSocketConnected.value) {
    showNotification('❌', 'Erreur', 'WebSocket non connecté');
    console.error('❌ Socket non disponible ou non connecté');
    return;
  }

  isLoading.value = true;

  try {
    socket.emit('call_next', {
      counterId: selectedCounterId.value,
      counterName: currentCounterName.value,
      agencyId: agencyId
    });

    console.log('📞 Appel du suivant pour:', currentCounterName.value);
  } catch (error) {
    console.error('❌ Erreur lors de l\'appel:', error);
    showNotification('❌', 'Erreur', 'Échec de l\'appel');
  } finally {
    setTimeout(() => {
      isLoading.value = false;
    }, 1000);
  }
};

// Call Specific Ticket (New Feature)
const callSpecificTicket = (ticket) => {
  if (!ticket) {
    console.error('❌ Ticket manquant');
    return;
  }

  if (!selectedCounterId.value) {
    showNotification('⚠️', 'Attention', 'Aucun guichet sélectionné');
    return;
  }

  if (!socket || !isSocketConnected.value) {
    showNotification('❌', 'Erreur', 'WebSocket non connecté');
    console.error('❌ Socket non disponible ou non connecté');
    return;
  }

  try {
    socket.emit('call_specific_ticket', {
      ticketId: ticket._id,
      counterId: selectedCounterId.value,
      counterName: currentCounterName.value,
      agencyId: agencyId
    });

    showNotification('📞', 'Ticket Appelé', `${ticket.number} appelé au ${currentCounterName.value}`);
    console.log('📞 Appel direct du ticket:', ticket.number);
  } catch (error) {
    console.error('❌ Erreur lors de l\'appel direct:', error);
    showNotification('❌', 'Erreur', 'Échec de l\'appel');
  }
};

// Recall Ticket
const recallTicket = (ticket) => {
  if (!ticket) {
    console.error('❌ Ticket manquant');
    return;
  }

  if (!socket || !isSocketConnected.value) {
    showNotification('❌', 'Erreur', 'WebSocket non connecté');
    console.error('❌ Socket non disponible ou non connecté');
    return;
  }

  try {
    socket.emit('recall_ticket', {
      ticketId: ticket._id,
      counterName: currentCounterName.value,
      agencyId: agencyId
    });

    showNotification('🔁', 'Rappel', `Ticket ${ticket.number} rappelé`);
    console.log('🔁 Rappel ticket:', ticket.number);
  } catch (error) {
    console.error('❌ Erreur lors du rappel:', error);
    showNotification('❌', 'Erreur', 'Échec du rappel');
  }
};

// Mark Absent
const markAbsent = (ticket) => {
  if (!ticket) {
    console.error('❌ Ticket manquant');
    return;
  }

  if (!socket || !isSocketConnected.value) {
    showNotification('❌', 'Erreur', 'WebSocket non connecté');
    console.error('❌ Socket non disponible ou non connecté');
    return;
  }

  if (confirm(`Marquer le ticket ${ticket.number} comme absent ?`)) {
    try {
      socket.emit('mark_absent', {
        ticketId: ticket._id,
        agencyId: agencyId
      });

      showNotification('❌', 'Absent', `Ticket ${ticket.number} marqué absent`);
      console.log('❌ Ticket absent:', ticket.number);
    } catch (error) {
      console.error('❌ Erreur lors du marquage absent:', error);
      showNotification('❌', 'Erreur', 'Échec du marquage');
    }
  }
};

// Calculate wait time
const getWaitTime = (createdAt) => {
  const start = new Date(createdAt);
  const now = new Date();
  return Math.floor((now - start) / 60000);
};

// Format call time (HH:MM)
const formatCallTime = (calledAt) => {
  if (!calledAt) return '--:--';
  const date = new Date(calledAt);
  return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

// WebSocket Initialization
const initWebSocket = () => {
  if (!token) {
    console.error('❌ Token manquant pour WebSocket');
    showNotification('❌', 'Erreur', 'Token manquant');
    return;
  }

  console.log('🔌 Initialisation WebSocket...');

  try {
    socket = io(WS_URL, {
      query: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });

    socket.on('connect', () => {
      console.log('✅ WebSocket connecté - ID:', socket.id);
      isSocketConnected.value = true;
      showNotification('✅', 'Connecté', 'WebSocket actif');
    });

    socket.on('connect_error', (error) => {
      console.error('❌ WS CONNECTION ERROR:', error.message);
      isSocketConnected.value = false;
      showNotification('❌', 'Erreur Connexion', 'Impossible de se connecter au serveur');
    });

    socket.on('error', (error) => {
      console.error('❌ WS ERROR:', error);
      showNotification('❌', 'Erreur', 'Erreur WebSocket');
    });

    socket.on('ticket_called', (data) => {
      console.log('📞 Ticket appelé:', data);

      if (data.ticket.counterId === selectedCounterId.value) {
        currentTicket.value = data.ticket;
        showNotification('📞', 'Ticket Appelé', `${data.ticket.number} au ${data.counterName}`);
      } else {
        // Ticket called on another counter - update recent calls
        fetchRecentOtherCounterCalls();
      }

      fetchWaitingTickets();
    });

    socket.on('queue_updated', () => {
      console.log('🔄 File mise à jour');
      fetchWaitingTickets();
    });

    socket.on('queue_empty', () => {
      showNotification('ℹ️', 'File Vide', 'Aucun ticket en attente');
    });

    socket.on('new_ticket_waiting', (data) => {
      console.log('🆕 Nouveau ticket:', data);
      fetchWaitingTickets();
    });

    socket.on('disconnect', (reason) => {
      console.log('❌ WebSocket déconnecté:', reason);
      isSocketConnected.value = false;
      showNotification('⚠️', 'Déconnecté', 'WebSocket déconnecté');
    });

    socket.on('reconnect', (attemptNumber) => {
      console.log('🔄 WebSocket reconnecté après', attemptNumber, 'tentatives');
      isSocketConnected.value = true;
      showNotification('✅', 'Reconnecté', 'Connexion rétablie');
    });

  } catch (error) {
    console.error('❌ Erreur initialisation WebSocket:', error);
    showNotification('❌', 'Erreur', 'Échec initialisation WebSocket');
  }
};

// Show Notification
const showNotification = (icon, title, message) => {
  notification.value = { icon, title, message };
  setTimeout(() => {
    notification.value = null;
  }, 3000);
};
</script>

<style scoped>
@keyframes slide-in {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.animate-slide-in {
  animation: slide-in 0.3s ease-out;
}
</style>
